import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Resumable new-engagement wizard drafts (T-0128 sub-item C / PR #153).
 *
 *   - GET    /api/v1/me/engagement-drafts          → list per-user.
 *   - PUT    /api/v1/me/engagement-drafts/:id      → upsert (201/200).
 *   - DELETE /api/v1/me/engagement-drafts/:id      → hard delete (204).
 *
 * Per-user scratch space, pre-engagement (no `engagement_id` yet). The
 * service layer enforces `user_id = caller.userId`; cross-user accesses
 * surface as 404. The wizard PUTs on step changes so a reload can pick
 * up where the user left off, and DELETEs after the engagement mints.
 */

export const EngagementDraftSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  draft_state: z.unknown(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type EngagementDraft = z.infer<typeof EngagementDraftSchema>;

const ListEngagementDraftsResponseSchema = z.object({
  drafts: z.array(EngagementDraftSchema),
});
export type ListEngagementDraftsResponse = z.infer<typeof ListEngagementDraftsResponseSchema>;

export function listEngagementDrafts(): Promise<ListEngagementDraftsResponse> {
  return apiClient('/api/v1/me/engagement-drafts', ListEngagementDraftsResponseSchema);
}

export function useEngagementDraftsQuery() {
  return useQuery({
    queryKey: ['me', 'engagement-drafts'] as const,
    queryFn: listEngagementDrafts,
    staleTime: 30_000,
  });
}

export function upsertEngagementDraft(
  draftId: string,
  draftState: unknown,
): Promise<EngagementDraft> {
  return apiClient(
    `/api/v1/me/engagement-drafts/${encodeURIComponent(draftId)}`,
    EngagementDraftSchema,
    { method: 'PUT', body: { draft_state: draftState } },
  );
}

export function deleteEngagementDraft(draftId: string): Promise<void> {
  return apiClient<void>(
    `/api/v1/me/engagement-drafts/${encodeURIComponent(draftId)}`,
    z.any().transform(() => undefined) as z.ZodType<void>,
    { method: 'DELETE' },
  );
}

export function useUpsertEngagementDraftMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, draftState }: { draftId: string; draftState: unknown }) =>
      upsertEngagementDraft(draftId, draftState),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'engagement-drafts'] });
    },
  });
}

export function useDeleteEngagementDraftMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId }: { draftId: string }) => deleteEngagementDraft(draftId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'engagement-drafts'] });
    },
  });
}
